import { Box, Flex, Progress } from '@chakra-ui/react';
import { Tabs, Tags, Text } from '@kula-ai/components';
import {
  ArrowSquareUpRight,
  Briefcase01,
  Calendar,
  Monitor01,
  Users01,
} from '@kula-ai/icons-core';
import type { JobBlockData, JobPill } from '../../../types';
import { JobDetailsTab } from './JobDetailsTab';
import { JobTeamTab } from './JobTeamTab';
import { JobInterviewPlanTab } from './JobInterviewPlanTab';
import { JobPostingTab } from './JobPostingTab';
import type { JobBlockProps, JobStatusTagVariant } from './JobBlock.types';

const STATUS_VARIANT: Record<JobBlockData['status'], JobStatusTagVariant> = {
  draft: 'warning',
  unlisted: 'neutral',
  live: 'success',
};

const STATUS_LABEL: Record<JobBlockData['status'], string> = {
  draft: 'Draft',
  unlisted: 'Unlisted',
  live: 'Live',
};

const clamp = (n: number, min: number, max: number): number =>
  Math.min(Math.max(n, min), max);

const resolvePills = (data: JobBlockData): JobPill[] =>
  data.pills && data.pills.length > 0
    ? data.pills
    : [{ label: STATUS_LABEL[data.status], tone: STATUS_VARIANT[data.status] }];

const TAB_ICON_PROPS = { size: 14, color: 'currentColor' } as const;

const buildTabItems = (data: JobBlockData) => {
  const items = [
    {
      value: 'details',
      label: 'Details',
      leftElement: <Briefcase01 {...TAB_ICON_PROPS} />,
      panel: <JobDetailsTab data={data.tabs.details} />,
    },
  ];

  if (data.tabs.team) {
    items.push({
      value: 'team',
      label: 'Team',
      leftElement: <Users01 {...TAB_ICON_PROPS} />,
      panel: <JobTeamTab data={data.tabs.team} />,
    });
  }

  if (data.tabs.interviewPlan) {
    items.push({
      value: 'interview-plan',
      label: 'Interview Plan',
      leftElement: <Calendar {...TAB_ICON_PROPS} />,
      panel: <JobInterviewPlanTab data={data.tabs.interviewPlan} />,
    });
  }

  if (data.tabs.jobPosting) {
    items.push({
      value: 'job-posting',
      label: 'Job Posting',
      leftElement: <Monitor01 {...TAB_ICON_PROPS} />,
      panel: <JobPostingTab data={data.tabs.jobPosting} />,
    });
  }

  return items;
};

export const JobBlock = ({ data }: JobBlockProps) => {
  const items = buildTabItems(data);
  const pills = resolvePills(data);
  const completion =
    typeof data.completionPercent === 'number'
      ? clamp(data.completionPercent, 0, 100)
      : null;

  return (
    <Flex
      direction="column"
      gap="12px"
      data-test-id={`agentforge-job-block-${data.jobId}`}
    >
      <Box
        as="a"
        href={data.ctaHref}
        bg="secondary-50"
        border="1px solid"
        borderColor="secondary-100"
        borderRadius="8px"
        px="12px"
        py="10px"
        _hover={{ bg: 'secondary-100' }}
      >
        <Flex align="center" justify="space-between" gap="12px">
          <Text variant="span" className="text-primary-700 font-medium">
            {data.ctaLabel}
          </Text>
          <Box color="primary-700">
            <ArrowSquareUpRight size={16} color="currentColor" />
          </Box>
        </Flex>
      </Box>

      {data.description && (
        <Text variant="p" className="text-secondary-700 whitespace-pre-wrap">
          {data.description}
        </Text>
      )}

      <Box
        bg="base-white"
        border="1px solid"
        borderColor="secondary-100"
        borderRadius="10px"
        overflow="hidden"
      >
        <Box px="14px" pt="12px">
          <Flex align="center" gap="8px" mb="6px" wrap="wrap">
            <Text variant="span" className="text-secondary-900 font-semibold">
              {data.title}
            </Text>
            {pills.map((pill) => (
              <Tags key={pill.label} label={pill.label} variant={pill.tone} />
            ))}
          </Flex>
          {data.subtitle && (
            <Text
              variant="span"
              className="text-secondary-600"
              data-test-id="agentforge-job-subtitle"
            >
              {data.subtitle}
            </Text>
          )}
          {completion !== null && (
            <Flex
              align="center"
              gap="8px"
              mt="10px"
              data-test-id="agentforge-job-completion"
            >
              <Text variant="span" className="text-secondary-700">
                Completion status:
              </Text>
              <Progress
                value={completion}
                flex="1"
                size="sm"
                borderRadius="full"
                colorScheme="purple"
                aria-label="Completion status"
                aria-valuenow={completion}
              />
              <Text variant="span" className="text-secondary-900 font-medium">
                {completion}%
              </Text>
            </Flex>
          )}
        </Box>
        <Box px="14px" pb="14px" pt="10px">
          <Tabs.Root variant="default" defaultValue="details" items={items} />
        </Box>
      </Box>
    </Flex>
  );
};
